import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const day = (iso) => (iso ? new Date(/[Z+]/.test(iso.slice(19)) ? iso : `${iso}Z`).toLocaleDateString() : "—");
const money = (v, cur) => (v != null ? `${v.toFixed(2)} ${cur}` : "unknown");

const ReviewDialog = ({ p, mode, onClose, onDone }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const approve = mode === "approve";
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/payments/${p.payment_id}/${approve ? "approve" : "refund-review"}`, approve ? { note } : {});
      toast.success(approve ? `Approved. ${p.email || "The member"} is unlocked` : `Refunded ${money(p.charged, p.currency)}`);
      onDone();
    } catch (e) { toast.error(errMsg(e, approve ? "Could not approve" : "Refund failed")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid={`review-${mode}-dialog`}>
        <DialogHeader>
          <DialogTitle>{approve ? "Approve this payment?" : `Refund ${money(p.charged, p.currency)}?`}</DialogTitle>
          <DialogDescription className="[overflow-wrap:anywhere]">
            {approve ? `This marks the payment as paid, unlocks ${p.email || "the member"} and emails them a receipt.`
              : `This refunds ${p.email || "the payer"} in full through PayPal and emails them a refund confirmation.`}
          </DialogDescription>
        </DialogHeader>
        {approve && <textarea className="mm-input min-h-[80px]" maxLength={500} placeholder="Optional note for your records (why you approved it)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="review-approve-note" />}
        <div className="flex justify-end gap-2">
          <button className="mm-btn-ghost !py-2 !px-4 text-sm" disabled={busy} onClick={onClose} data-testid={`review-${mode}-cancel`}>Cancel</button>
          <button className="mm-btn !py-2 !px-4 text-sm inline-flex items-center gap-2" disabled={busy} onClick={go} data-testid={`review-${mode}-confirm`}>
            {busy && <Loader2 size={14} className="animate-spin" />} {approve ? "Approve and unlock" : "Refund now"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ p, onAct }) => (
  <div className="mm-soft p-3 text-sm flex flex-wrap items-start justify-between gap-2" data-testid={`review-payment-${p.payment_id}`}>
    <div className="min-w-0">
      <div className="font-semibold truncate">{p.name || "Guest"} · <span className="font-normal text-mm-muted">{p.email || "no email"}</span></div>
      <div className="text-xs text-mm-muted">
        {p.receipt_id} · charged <b className="text-mm-error" data-testid={`review-charged-${p.payment_id}`}>{money(p.charged, p.currency)}</b> (expected {money(p.expected, "USD")}) · {day(p.review_at || p.created_at)}
      </div>
    </div>
    <div className="flex gap-2 shrink-0">
      <button className="mm-btn !py-1.5 !px-3 text-xs" onClick={() => onAct(p, "approve")} data-testid={`review-approve-${p.payment_id}`}>Approve</button>
      <button className="mm-btn-ghost !py-1.5 !px-3 text-xs" onClick={() => onAct(p, "refund")} data-testid={`review-refund-${p.payment_id}`}>Refund</button>
    </div>
  </div>
);

// Unlock payments PayPal captured with an unexpected amount; hidden when there are none
export const PaymentReview = ({ onChange }) => {
  const [items, setItems] = useState(null);
  const [act, setAct] = useState(null);
  const load = useCallback(() => api.get("/admin/payments/review").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!items?.length) return null;
  return (
    <div className="mm-card p-5 mt-6 border-mm-error/40" data-testid="admin-payment-review">
      <div className="flex items-center gap-2 font-semibold">
        <AlertTriangle size={18} className="text-mm-error" /> Payments needing review
        <span className="rounded-full px-2 py-0.5 text-xs bg-mm-error text-white" data-testid="review-count">{items.length}</span>
      </div>
      <p className="text-xs text-mm-muted mt-1">PayPal charged a different amount than expected, so access wasn't unlocked. Approve to unlock, or refund the payer.</p>
      <div className="space-y-2 mt-3">{items.map((p) => <Row key={p.payment_id} p={p} onAct={(x, mode) => setAct({ p: x, mode })} />)}</div>
      {act && <ReviewDialog p={act.p} mode={act.mode} onClose={() => setAct(null)} onDone={() => { setAct(null); load(); onChange?.(); }} />}
    </div>
  );
};
